import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { Link } from 'react-router-dom';
import { format } from 'date-fns';
import { DELETE_TINYCMS_ARTICLE, MARK_TOPIC_NOT_DUPLICATE } from '../../graphql/mutations';
import type { ContentDuplicate } from '../../types/graphql';
import { categoryList } from '../cms/categories';
import { showInkToast } from '../cms/InkToast';
import { askInk } from '../cms/InkConfirm';

const when = (v?: string | null) => {
  if (!v) return '';
  const d = new Date(/^\d{10,}$/.test(v) ? Number(v) : v);
  return Number.isNaN(d.getTime()) ? '' : format(d, 'MMM d, h:mm a');
};

/**
 * "Possible duplicate" on the article screen (Prototype v14): the other live
 * articles with this title and body, and the two ways out — keep this one and
 * take the copies off the site, or say they're not duplicates.
 *
 * The copies go to Recently deleted (the app's delete: readers stop seeing
 * them, restorable for 30 days as drafts), so the notice says that instead of
 * offering an Undo that would bring them back unpublished.
 */
export function DuplicateCard({
  accountId,
  articleId,
  duplicates,
  onChanged,
}: {
  accountId: string;
  articleId: string;
  duplicates: ContentDuplicate[];
  onChanged: () => void | Promise<unknown>;
}) {
  const [deleteArticle] = useMutation(DELETE_TINYCMS_ARTICLE);
  const [markNotDuplicate] = useMutation(MARK_TOPIC_NOT_DUPLICATE);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  if (!duplicates.length) return null;
  const n = duplicates.length;
  const copies = `${n} ${n === 1 ? 'copy' : 'copies'}`;

  const keep = async () => {
    if (
      !(await askInk({
        title: `Keep this article and delete the other ${copies}?`,
        body: 'Readers stop seeing them.',
        note: 'They stay in Recently deleted for 30 days.',
        confirmLabel: 'Keep this one',
        danger: true,
      }))
    )
      return;
    setBusy(true);
    setError('');
    let done = 0;
    const failed: string[] = [];
    for (const d of duplicates) {
      try {
        await deleteArticle({ variables: { accountId, id: d.id } });
        done += 1;
      } catch (err) {
        failed.push(err instanceof Error ? err.message : 'That didn’t work.');
      }
    }
    await onChanged();
    setBusy(false);
    if (done) {
      showInkToast(
        `Kept this one. Moved ${done} ${done === 1 ? 'copy' : 'copies'} to Recently deleted; readers no longer see ${done === 1 ? 'it' : 'them'}.`
      );
    }
    if (failed.length) setError(failed[0]);
  };

  const notDuplicate = async () => {
    setBusy(true);
    setError('');
    try {
      await markNotDuplicate({ variables: { accountId, id: articleId } });
      await onChanged();
      showInkToast('Marked as not duplicates.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'That didn’t work.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-part="duplicates" className="ink-rail-block">
      <div className="ink-check">
        <span className="ink-check-icon" style={{ color: 'var(--ink-amber)' }}>
          ●
        </span>
        <span>
          Possible duplicate of {n} live {n === 1 ? 'article' : 'articles'}
        </span>
        <span className="ink-check-sev" style={{ color: 'var(--ink-amber)' }}>
          Warning
        </span>
      </div>
      <div className="ink-dup-card">
        <div className="ink-dup-head">Same title and body as:</div>
        {duplicates.map((d) => (
          <Link key={d.id} to={`/content/${d.id}`} className="ink-dup-link">
            {[
              categoryList(d.category)[0],
              when(d.publishedDate) && `published ${when(d.publishedDate)}`,
            ]
              .filter(Boolean)
              .join(' · ') ||
              d.title ||
              'Untitled'}{' '}
            →
          </Link>
        ))}
        <button
          type="button"
          className="ink-btn ink-btn-primary"
          disabled={busy}
          onClick={() => void keep()}
        >
          Keep this, delete {n}
        </button>
        <button
          type="button"
          className="ink-btn ink-btn-quiet"
          disabled={busy}
          onClick={() => void notDuplicate()}
        >
          Not a duplicate
        </button>
        {error ? (
          <div className="ink-rail-note" style={{ color: 'var(--ink-red)' }}>
            {error}
          </div>
        ) : null}
      </div>
    </section>
  );
}
